// Property endpoint functions. Async, call `sellerApi`, throw `AppError` (mapped
// by the interceptor), and return DTOs — the parser runs in the query hook.
//
// All reads: the seller has no property write path (admin-service owns the
// property lifecycle), so there are no mutations in this module.
import { sellerApi } from '@core/api/httpClient'
import { propertyEndpointPaths } from './propertyEndpointPaths'
import type {
  ApiEnvelope,
  PaginatedEnvelope,
  PortfolioStatusDto,
  PropertyDetailDto,
  PropertyListDto,
  PropertySettlementDto,
  PropertyStatusCountsDto,
  PropertySummaryDto,
  PropertyWindowExtensionDto,
} from './propertyContracts'

// Query params accepted by the paginated list endpoint. The quick filter and
// the search are applied server-side before the page is cut, so totals are
// right. Omitted when empty — no bucket is All properties.
export interface GetPropertiesParams {
  page: number
  limit: number
  bucket: string | null
  search: string
}

export async function getProperties(params: GetPropertiesParams): Promise<PropertyListDto> {
  const { data } = await sellerApi.get<PaginatedEnvelope<PropertySummaryDto>>(
    propertyEndpointPaths.PROPERTIES,
    {
      params: {
        page: params.page,
        limit: params.limit,
        ...(params.bucket && { bucket: params.bucket }),
        ...(params.search && { search: params.search }),
      },
    },
  )
  return { items: data.data, pagination: data.pagination }
}

export async function getProperty(id: string): Promise<PropertyDetailDto> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertyDetailDto>>(
    propertyEndpointPaths.property(id),
  )
  return data.data
}

export async function getPropertyStatusCounts(): Promise<PropertyStatusCountsDto> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertyStatusCountsDto>>(
    propertyEndpointPaths.STATUS_COUNTS,
  )
  return data.data
}

export async function getPortfolioStatus(): Promise<PortfolioStatusDto> {
  const { data } = await sellerApi.get<ApiEnvelope<PortfolioStatusDto>>(
    propertyEndpointPaths.PORTFOLIO_STATUS,
  )
  return data.data
}

export async function getPropertyWindowExtension(id: string): Promise<PropertyWindowExtensionDto> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertyWindowExtensionDto>>(
    propertyEndpointPaths.propertyExtension(id),
  )
  return data.data
}

export async function getPropertySettlement(id: string): Promise<PropertySettlementDto> {
  const { data } = await sellerApi.get<ApiEnvelope<PropertySettlementDto>>(
    propertyEndpointPaths.propertySettlement(id),
  )
  return data.data
}
